import { useCallback, useMemo } from 'react';
import { useQueryParams } from '@shared/hooks/useQueryParams';
import { RatesFilters } from '../models/rate.types';

const RATES_FILTER_CONFIG = {
  search: { type: 'string' as const, default: '' },
  status: {
    type: 'string' as const,
    default: 'all' as 'active' | 'inactive' | 'all',
  },
};

export const useRatesFilters = () => {
  const { params, updateParam, updateParams, resetParams, hasParams } =
    useQueryParams(RATES_FILTER_CONFIG);

  const handleSearchChange = useCallback(
    (value: string) => {
      updateParam('search', value);
    },
    [updateParam]
  );

  const handleStatusChange = useCallback(
    (value: 'active' | 'inactive' | 'all') => {
      updateParam('status', value);
    },
    [updateParam]
  );

  const handleClearFilters = useCallback(() => {
    resetParams();
  }, [resetParams]);

  // Transform to API filters format
  const apiFilters: RatesFilters = useMemo(() => {
    const filters: RatesFilters = {
      search: params.search || undefined,
    };

    // Convert status string to is_active boolean
    if (params.status === 'active') {
      filters.is_active = true;
    } else if (params.status === 'inactive') {
      filters.is_active = false;
    }

    return filters;
  }, [params.search, params.status]);

  return {
    // Current filter values
    search: params.search,
    status: params.status as 'active' | 'inactive' | 'all',

    // Filter handlers
    handleSearchChange,
    handleStatusChange,
    handleClearFilters,

    // For API calls
    apiFilters,

    // Helpers
    hasActiveFilters: hasParams,
    updateParams,
  };
};
